import { HttpResponse, HttpService } from '@application/http.service';
import type { AuthMethodApiModel, SmsConfirmCodeApiResult, SmsStartAuthModel } from '@visiba/api-vcs-client-v2';
import { Injectable, Scopes } from '@visiba/instantiation';
import { ParameterHandler } from '@visiba/std';
import { ApiBaseClient } from './_api_base_client';

@Injectable({
	scope: Scopes.Transient,
})
export class AuthApiService extends ApiBaseClient {
	constructor(private readonly httpService: HttpService) {
		super('units');
	}

	public getMethods(licenceHolderId: number): HttpResponse<
		{
			Scheme: string;
			Name: string;
			Description: string;
			UseIframe: boolean;
		}[]
	> {
		return this.httpService.get(`/client/auth/idp/${licenceHolderId}/methods`);
	}

	public async getAuthMethods(
		unitId: number,
		countryCode: string,
		callTicket: Nullable<string> = null,
	): HttpResponse<AuthMethodApiModel[]> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({
			device: 'computer',
			countryCode,
		});
		if (callTicket != null) parameterHandler.append({ usage: 'Outsider' });

		return this.httpService.get(`${this.baseUri}/${unitId}/Auth/Methods${parameterHandler.getString()}`);
	}

	public async sendSMSCode(unitId: number, number: string, countryCode?: string): HttpResponse<SmsStartAuthModel> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ number: number });
		if (countryCode) parameterHandler.append({ countryCode });

		return this.httpService.post(`${this.baseUri}/${unitId}/Auth/Methods/Sms/SendCode${parameterHandler.getString()}`, null);
	}

	public async sendSMSCodeWithCallticket(unitId: number, countryCode: string, callTicket: string): HttpResponse<SmsStartAuthModel> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({
			callTicket: callTicket,
			countryCode: countryCode,
		});

		return this.httpService.post(
			`${this.baseUri}/${unitId}/Auth/Methods/Sms/SendCodeWithCallTicket${parameterHandler.getString()}`,
			null,
		);
	}

	public async resendSmsCode(unitId: number, authAttemptId: Guid): AsyncResult<unknown> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ attemptId: authAttemptId });

		return this.httpService.post(`${this.baseUri}/${unitId}/Auth/Methods/Sms/ReSendCode${parameterHandler.getString()}`, null);
	}

	public async getPreRegisteredNumber(receptionId: number, callTicket: string): HttpResponse<number> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ callticket: callTicket });

		return this.httpService.get(`${this.baseUri}/${receptionId}/Auth/Methods/Sms/PreRegisteredNumber${parameterHandler.getString()}`);
	}

	public async confirmSmsCode(
		unitId: number,
		attemptId: Guid,
		code: string,
		sessionLifetime?: number,
	): HttpResponse<SmsConfirmCodeApiResult> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ attemptId: attemptId, code: code });
		if (sessionLifetime) parameterHandler.append({ sessionLifetime: sessionLifetime });

		return this.httpService.post(`${this.baseUri}/${unitId}/Auth/Methods/Sms/ConfirmCode${parameterHandler.getString()}`, null);
	}

	public async beginNoAuth(unitId: number, callTicket: string, countryCode?: string): HttpResponse<SmsStartAuthModel> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ callTicket: callTicket });
		if (countryCode) parameterHandler.append({ countryCode: countryCode });

		return this.httpService.post(`${this.baseUri}/${unitId}/Auth/Methods/NoAuth/StartAuth${parameterHandler.getString()}`, null);
	}

	public async confirmNoAuthIdentity(unitId: number, attemptId: Guid, commonName: string): HttpResponse<SmsConfirmCodeApiResult> {
		const parameterHandler = new ParameterHandler();
		parameterHandler.append({ authAttempt: attemptId, commonName: encodeURIComponent(commonName) });

		return this.httpService.post(`${this.baseUri}/${unitId}/Auth/Methods/NoAuth/ConfirmIdentity${parameterHandler.getString()}`, null);
	}
}
